import type { RenderState, Stage } from "./display";
import { Event, broadcast } from "./events";

/**
 * Fixed-step frame driver. Flash advances the display list at
 * `stage.frameRate`, so game logic must not be tied to real elapsed time;
 * the accumulator keeps logic deterministic and only drops visual frames.
 */
export function startFrameLoop(
  stage: Stage,
  ctx: CanvasRenderingContext2D,
  backgroundColor = "#000000",
): void {
  let previous = performance.now();
  let accumulator = 0;
  // reused every frame: the walk mutates and restores it in place.
  const state: RenderState = { ctx, alpha: 1, colorTransform: null };

  const tick = (now: number) => {
    requestAnimationFrame(tick);
    const step = 1000 / stage.frameRate;
    accumulator += now - previous;
    previous = now;
    if (accumulator > step * 8) accumulator = step; // tab was backgrounded

    let steps = 0;
    while (accumulator >= step && steps < MAX_STEPS_PER_TICK) {
      accumulator -= step;
      steps++;
      broadcast(Event.ENTER_FRAME);
    }
    if (steps === 0) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.filter = "none";
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, stage.stageWidth, stage.stageHeight);
    state.alpha = 1;
    state.colorTransform = null;
    stage.$render(state);
  };

  requestAnimationFrame(tick);
}

const MAX_STEPS_PER_TICK = 4;
